import { useEffect, useState, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, LayoutGrid, List, Share2, X, Search, CheckCircle2, Circle, Plus, ArrowRight, Trash2, AlertTriangle } from "lucide-react";
import { Modal, Pagination, Select } from "antd";
import { toast } from "react-toastify";
import { MockTradeAccountItem, TradeAccountApi, Team, TradeAccount } from "../type";
import { getUserTradeAccounts, addTeamTradeAccount, getTeamDetail, ConvertTeamDetailToTradeAccountList, removeTeamTradeAccount } from "../funtion";
import { mixPostV1, mixPostV2, mixGetV2 } from "services/mixin";
import InfoDeclarationModal from "./AccountInfoDeclareModal";
import { API_URL } from "env";

const PLATFORM_BADGE_CLASS: Record<string, string> = {
  MT4: "team-accounts-tab__badge--mt4",
  MT5: "team-accounts-tab__badge--mt5",
  CTRADER: "team-accounts-tab__badge--ctrader",
};

const parseDate = (dateStr: string) => {
  const [d, m, y] = dateStr.split("/");
  return new Date(Number(y), Number(m) - 1, Number(d)).getTime();
};

const TeamAccountsTab: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [teamDetail, setTeamDetail] = useState<Team | null>(null);
  const [accounts, setAccounts] = useState<TradeAccount[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const [isLinkAccountModalOpen, setIsLinkAccountModalOpen] = useState(false);
  const [myTradeAccounts, setMyTradeAccounts] = useState<TradeAccountApi[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isNewAccountModalOpen, setIsNewAccountModalOpen] = useState(false);
  const [newAccountId, setNewAccountId] = useState("");
  const [newAccountNickname, setNewAccountNickname] = useState("");
  const [newAccountBalance, setNewAccountBalance] = useState(0);
  const [newAccountPlatform, setNewAccountPlatform] = useState("");
  const [newAccountBroker, setNewAccountBroker] = useState("");
  const [newAccountType, setNewAccountType] = useState("");
  const [isAddNewInfoToAccountModalOpen, setIsAddNewInfoToAccountModalOpen] = useState(false);
  const [brokerOptions, setBrokerOptions] = useState<{ value: string, label: string }[]>([
    { value: "FTMO", label: "FTMO" },
    { value: "Ecmarkets", label: "EC Markets" },
    { value: "IC Markets", label: "IC Markets" },
  ]);
  const [savingNewAccount, setSavingNewAccount] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<TradeAccount | null>(null);

  const groupedAccounts = accounts
    .filter((account) => account.can_view_history)
    .reduce<Record<string, TradeAccount[]>>((acc, account) => {
      const shareByName = typeof account.share_by === "object" && account.share_by ? account.share_by.fullname : account.share_by || "";
      const key = shareByName || "";
      if (!acc[key]) acc[key] = [];
      acc[key].push(account);
      return acc;
    }, {});

  Object.keys(groupedAccounts).forEach((key) => {
    groupedAccounts[key].sort((a, b) => parseDate(b.share_date || "") - parseDate(a.share_date || ""));
  });

  const toggleGroup = (sharedBy: string) => {
    setExpandedGroups((prev) => ({ ...prev, [sharedBy]: !prev[sharedBy] }));
  };

  const fetchMyTradeAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const accounts = await getUserTradeAccounts();
      if (id) {
        const teamDetail = await getTeamDetail(Number(id));
        const teamTradeAccounts = ConvertTeamDetailToTradeAccountList(teamDetail);
        setMyTradeAccounts(
          (accounts || []).filter(
            (account) => !teamTradeAccounts.some((teamAccount) => teamAccount.account_number === account.account_number)
          )
        );
      } else {
        setMyTradeAccounts(accounts || []);
      }
    } catch {
      setMyTradeAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const openLinkAccountModal = async () => {
    setIsLinkAccountModalOpen(true);
    setSelectedAccountNumber(null);
    setSearchQuery("");
    await fetchMyTradeAccounts();
  };

  const closeLinkAccountModal = () => {
    setIsLinkAccountModalOpen(false);
    setSelectedAccountNumber(null);
    setSearchQuery("");
  };

  const handleShareAccount = async () => {
    if (!selectedAccountNumber || !id) return;
    setSharing(true);
    try {
      const data = await addTeamTradeAccount(Number(id), selectedAccountNumber);
      // Kiểm tra nếu API trả về lỗi (isError được set ở hàm addTeamTradeAccount)
      if (data && data.error && data.error.length > 0) {
        // Kiểm tra lỗi validation riêng cho balance
        if (data.require_balance) {
          setIsAddNewInfoToAccountModalOpen(true);
        } else {
          // Các trường hợp lỗi hệ thống hoặc lỗi khác từ backend
          toast.error(data.message || t("SomethingWentWrong"));
        }
      } else if (data) {
        // Nếu API thành công (không có isError)
        toast.success(t("AccountSharedSuccessfully"));
        closeLinkAccountModal();
      }
    } catch (error) {
      console.error("Error sharing account to team", error);
    } finally {
      fetchTeamAccounts(Number(id));
      await fetchMyTradeAccounts(); 
      setSharing(false);
    }
  };

  const openNewAccountModal = async () => {
    setIsLinkAccountModalOpen(false);
    setNewAccountId("");
    setNewAccountNickname("");
    setNewAccountPlatform("");
    setNewAccountBroker("");
    setNewAccountType("");
    setIsNewAccountModalOpen(true);
    try {
      const response = await mixGetV2("/terminal-accounts/brokers");
      const brokers = (response.data || []).map((value: string) => ({ value, label: value }));
      if (brokers.length > 0) setBrokerOptions(brokers);
    } catch {
      // keep default broker options on failure
    }
  };

  const closeNewAccountModal = () => {
    setIsNewAccountModalOpen(false);
    setIsLinkAccountModalOpen(true);
  };

  const handleSaveNewAccount = async () => {
    if (!newAccountId || !newAccountNickname || !newAccountPlatform || !newAccountBroker) {
      toast.error(t("PleaseFillAllFields"));
      return;
    }
    const userData = localStorage.getItem("user");
    const userId = userData ? JSON.parse(userData).id : "";
    setSavingNewAccount(true);
    try {
      const res2 = await mixPostV2(`/terminal-accounts`, {
        login_id: newAccountId,
        app_type: newAccountPlatform,
        broker: newAccountBroker,
        balance: newAccountBalance,
        nickname: newAccountNickname,
        account_status: newAccountType || "",
      });
      const res = await mixPostV1(`/api/v1/be/trade_account`, {
        account_number: newAccountId,
        nickname: newAccountNickname,
        balance: newAccountBalance,
        platform: newAccountPlatform,
        broker: newAccountBroker,
        user_id: userId,
        account_status: newAccountType || "",
      });
      if (!res.status || !res2.status) {
        toast.error(res.detail?.[0]?.msg || res2.msg || t("FailedToShareAccount"));
        return;
      }
      toast.success(t("AccountSharedSuccessfully"));
      closeNewAccountModal();
      await fetchMyTradeAccounts();
    } catch {
      toast.error(t("FailedToShareAccount"));
    } finally {
      setSavingNewAccount(false);
    }
  };

  const filteredMyTradeAccounts = myTradeAccounts.filter((acc) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return (
      acc.account_number.toLowerCase().includes(query) ||
      (acc.nickname || "").toLowerCase().includes(query) ||
      acc.platform.toLowerCase().includes(query)
    );
  });

  function getTeamId(url: string): string | null {
    // Matches 'teams/' followed by everything up to the next '?', '&', or the end of the string
    const match = url.match(/teams\/([^?&#]*)/);

    if (!match) {
      return null;
    }

    // decodeURIComponent handles any URL encoding (e.g., %2F instead of /)
    return decodeURIComponent(match[1]);
  }

  const fetchTeamAccounts = async (teamId: number) => {
    const data = await getTeamDetail(teamId);
    setTeamDetail(data);
    setAccounts(data.trade_accounts);
  };

  useEffect(() => {
    const url = window.location.href;
    const teamId = getTeamId(url);
    if (teamId) {
      fetchTeamAccounts(Number(teamId));
    }
  }, []);

  const handleConfirmDeleteAccount = async () => {
    if (!accountToDelete) return;
    const url = window.location.href;
    const teamId = getTeamId(url);
    const res = await removeTeamTradeAccount(Number(teamId), accountToDelete.account_number);
    if (res.status) {
      toast.success(t("AccountDeletedSuccessfully"));
      await fetchTeamAccounts(Number(teamId));
    } else {
      toast.error(t("FailedToDeleteAccount"));
    }
    setAccountToDelete(null);
  };

  return (
    <div className="team-accounts-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <p className="team-accounts-tab__title text-lg font-bold m-0">
          {t("TradingAccounts")}
        </p>
        <div className="flex items-center gap-3">
          <div className="team-accounts-tab__view-toggle flex p-1 rounded-lg">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              title={t("GridView")}
              aria-label={t("GridView")}
              className={`team-accounts-tab__view-btn ${viewMode === "grid" ? "team-accounts-tab__view-btn--active" : ""}`}
            >
              <LayoutGrid size={18} />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              title={t("ListView")}
              aria-label={t("ListView")}
              className={`team-accounts-tab__view-btn ${viewMode === "list" ? "team-accounts-tab__view-btn--active" : ""}`}
            >
              <List size={18} />
            </button>
          </div>
          <button
            type="button"
            onClick={openLinkAccountModal}
            className="team-accounts-tab__link-btn px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            {t("LinkAccount")}
          </button>
        </div>
      </div>

      {viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(groupedAccounts).map(([sharedBy, accounts]) => {
            const isExpanded = expandedGroups[sharedBy];
            const visibleAccounts = isExpanded ? accounts : [accounts[0]];

            return visibleAccounts.map((acc, index) => (
              <div key={acc.account_number} className="team-accounts-tab__card rounded-xl p-6 flex flex-col" onClick={() => navigate(`/${acc.platform}/account-live-metrix/${acc.account_number}`)}>
                <div className="flex justify-between items-center mb-2">
                  <p className="team-accounts-tab__card-name text-[16px] font-bold uppercase tracking-wide m-0">
                    {acc.nickname}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className={`team-accounts-tab__badge ${PLATFORM_BADGE_CLASS[acc.platform]}`}>
                      {acc.platform}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAccountToDelete(acc);
                      }}
                      aria-label={t("DeleteAccount")}
                      className="team-accounts-tab__delete-btn flex items-center justify-center"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="team-accounts-tab__card-number font-medium text-base mb-2">
                  {acc.account_number}
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="team-accounts-tab__label">{t("Balance")}:</span>
                  <span className="team-accounts-tab__value font-semibold">{acc.balance}</span>
                </div>

                <hr className="team-accounts-tab__divider my-2" />

                <div className="space-y-2 text-sm flex-1">
                  <div className="flex justify-between items-center">
                    <span className="team-accounts-tab__label">{t("Sharedby")}:</span>
                    <span className="team-accounts-tab__value font-medium">{acc.share_by ? `${acc.share_by.last_name} ${acc.share_by.first_name}` : ""}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="team-accounts-tab__label">{t("Date")}:</span>
                    <span className="team-accounts-tab__value font-medium">{acc.share_date ? new Date(acc.share_date).toLocaleDateString('vi-VN') : ""}</span>
                  </div>
                </div>

                {index === 0 && accounts.length > 1 && (
                  <div className="team-accounts-tab__card-footer mt-4 pt-4">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroup(sharedBy)
                      }}
                      className="team-accounts-tab__expand-btn w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronDown size={16} className="rotate-180" /> {t("Collapse")}
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} /> {t("ViewMoreAccounts", { count: accounts.length - 1 })}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            ));
          })}
        </div>
      ) : (
        <div className="team-accounts-tab__table-card rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="team-accounts-tab__thead-row">
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider">{t("AccountName")}</th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider">{t("AccountNumber")}</th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider">{t("Balance")}</th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider">{t("Platform")}</th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider">{t("Date")}</th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider">{t("Action")}</th>
                </tr>
              </thead>
              <tbody className="team-accounts-tab__tbody">
                {Object.entries(groupedAccounts).filter(([sharedBy, accounts]) => accounts.length > 0).map(([sharedBy, accounts]) => {
                  const isExpanded = expandedGroups[sharedBy];
                  const visibleAccounts = isExpanded ? accounts : [accounts[0]];

                  return (
                    <Fragment key={sharedBy}>
                      {visibleAccounts.map((acc) => (
                        <tr key={acc.account_number} className="team-accounts-tab__row transition-colors" onClick={() => navigate(`/${acc.platform}/account-live-metrix/${acc.account_number}`)}>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className="team-accounts-tab__avatar w-7 h-7 rounded-full flex items-center justify-center font-bold text-[11px] mr-2" title={`${t("Sharedby")} ${acc.share_by?.fullname}`}>
                                {acc.share_by ? `${acc.share_by.last_name} ${acc.share_by.first_name}` : ""}
                              </div>
                              <div>
                                <span className="team-accounts-tab__card-name font-bold uppercase block">{acc.nickname}</span>
                                <span className="team-accounts-tab__sub text-xs">{acc.share_by ? `${acc.share_by.last_name} ${acc.share_by.first_name}` : ""}</span>
                              </div>
                            </div>
                          </td>
                          <td className="team-accounts-tab__value px-6 py-4 font-medium text-sm">{acc.account_number}</td>
                          <td className="team-accounts-tab__value px-6 py-4 font-semibold text-sm">{acc.balance}</td>
                          <td className="px-6 py-4">
                            <span className={`team-accounts-tab__badge ${PLATFORM_BADGE_CLASS[acc.platform]}`}>
                              {acc.platform}
                            </span>
                          </td>
                          <td className="team-accounts-tab__value px-6 py-4 text-sm">{acc.share_date ? new Date(acc.share_date).toLocaleDateString('vi-VN') : ""}</td>
                          <td className="px-6 py-4 text-sm">
                            <button
                              type="button"
                              onClick={() => setAccountToDelete(acc)}
                              className="team-accounts-tab__delete-action"
                            >
                              {t("DeleteAccount")}
                            </button>
                          </td>
                        </tr>
                      ))}

                      {accounts.length > 1 && (
                        <tr className="team-accounts-tab__toggle-row">
                          <td colSpan={6} className="px-6 py-2.5 text-center">
                            <button
                              type="button"
                              onClick={() => toggleGroup(sharedBy)}
                              className="team-accounts-tab__expand-btn-inline inline-flex items-center justify-center gap-1.5 py-1.5 px-4 rounded-md text-sm font-medium transition-colors"
                            >
                              {isExpanded ? (
                                <>
                                  <ChevronDown size={16} className="rotate-180" /> {t("CollapseAccountsOf", { name: sharedBy })}
                                </>
                              ) : (
                                <>
                                  <ChevronDown size={16} /> {t("ViewMoreAccountsOf", { count: accounts.length - 1, name: sharedBy })}
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Pagination
        className="!mt-8 flex justify-end"
        pageSize={10}
        total={accounts.length}
        showSizeChanger={false}
      />

      <Modal
        open={isLinkAccountModalOpen}
        onCancel={closeLinkAccountModal}
        footer={null}
        closable={false}
        centered
        width={480}
        className="team-accounts-tab__link-modal"
      >
        <div className="team-accounts-tab__link-modal-header flex items-center justify-between pb-4 mb-4">
          <div className="flex items-center gap-2">
            <Share2 size={20} className="team-accounts-tab__link-modal-icon" />
            <p className="team-accounts-tab__link-modal-title text-base font-bold m-0">
              {t("ShareAccountToTeam")}
            </p>
          </div>
          <button
            type="button"
            onClick={closeLinkAccountModal}
            aria-label={t("Cancel")}
            className="team-accounts-tab__link-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="team-accounts-tab__link-modal-search relative mb-4">
          <Search size={16} className="team-accounts-tab__link-modal-search-icon absolute" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("SearchAccount")}
            className="team-accounts-tab__link-modal-search-input w-full text-sm"
          />
        </div>

        <p className="team-accounts-tab__link-modal-section text-xs font-semibold uppercase tracking-wide mb-2">
          {t("YourAccounts")}
        </p>

        <div className="team-accounts-tab__link-modal-list flex flex-col gap-2 mb-4 overflow-y-auto">
          {loadingAccounts ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="team-accounts-tab__link-modal-skeleton flex items-center justify-between gap-3 w-full">
                <div className="flex flex-col gap-2 flex-1">
                  <div className="team-accounts-tab__link-modal-skeleton-line team-accounts-tab__link-modal-skeleton-line--title" />
                  <div className="team-accounts-tab__link-modal-skeleton-line team-accounts-tab__link-modal-skeleton-line--text" />
                  <div className="team-accounts-tab__link-modal-skeleton-line team-accounts-tab__link-modal-skeleton-line--text" />
                </div>
                <div className="team-accounts-tab__link-modal-skeleton-circle" />
              </div>
            ))
          ) : filteredMyTradeAccounts.length === 0 ? (
            <div className="team-accounts-tab__link-modal-empty text-sm text-center py-6">
              {t("NoTradeAccountsFound")}
            </div>
          ) : (
            filteredMyTradeAccounts.map((acc) => {
              const isSelected = selectedAccountNumber === acc.account_number;
              const platformKey = (acc.platform || "").toUpperCase();
              return (
                <button
                  key={acc.account_number}
                  type="button"
                  onClick={() => setSelectedAccountNumber(acc.account_number)}
                  className={`team-accounts-tab__link-modal-card flex items-center justify-between gap-3 text-left w-full ${isSelected ? "team-accounts-tab__link-modal-card--selected" : ""}`}
                >
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="team-accounts-tab__link-modal-card-name font-bold uppercase text-sm">
                        {acc.nickname || acc.account_number}
                      </span>
                      <span className={`team-accounts-tab__badge ${PLATFORM_BADGE_CLASS[platformKey] || ""}`}>
                        {platformKey}
                      </span>
                    </div>
                    <span className="team-accounts-tab__link-modal-card-number text-xs">
                      {acc.account_number}
                    </span>
                    <span className="team-accounts-tab__link-modal-card-balance text-xs font-semibold">
                      {t("Balance")}: {acc.balance}
                    </span>
                  </div>
                  {isSelected ? (
                    <CheckCircle2 size={20} className="team-accounts-tab__link-modal-check team-accounts-tab__link-modal-check--selected" />
                  ) : (
                    <Circle size={20} className="team-accounts-tab__link-modal-check" />
                  )}
                </button>
              );
            })
          )}
        </div>

        <hr className="team-accounts-tab__link-modal-divider mb-4" />

        <button
          type="button"
          onClick={openNewAccountModal}
          className="team-accounts-tab__link-modal-new flex items-center justify-between gap-3 w-full mb-4"
        >
          <div className="flex items-center gap-3">
            <span className="team-accounts-tab__link-modal-new-icon flex items-center justify-center">
              <Plus size={18} />
            </span>
            <div className="flex flex-col text-left">
              <span className="team-accounts-tab__link-modal-new-title text-sm font-semibold">
                {t("LinkNewAccount")}
              </span>
              <span className="team-accounts-tab__link-modal-new-desc text-xs">
                {t("LinkNewAccountDesc")}
              </span>
            </div>
          </div>
          <ArrowRight size={18} />
        </button>

        <div className="team-accounts-tab__link-modal-footer flex items-center justify-between pt-4">
          <span className="team-accounts-tab__link-modal-count text-sm">
            {t("SelectedAccountsCount", { count: selectedAccountNumber ? 1 : 0 })}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={closeLinkAccountModal}
              className="team-accounts-tab__link-modal-btn team-accounts-tab__link-modal-btn--cancel px-4 py-2 rounded-lg text-sm font-medium"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={handleShareAccount}
              disabled={!selectedAccountNumber || sharing}
              className="team-accounts-tab__link-modal-btn team-accounts-tab__link-modal-btn--share px-4 py-2 rounded-lg text-sm font-medium"
            >
              {t("Share")}
            </button>
          </div>
        </div>
      </Modal>

      <InfoDeclarationModal
        open={isAddNewInfoToAccountModalOpen}
        onCancel={() => setIsAddNewInfoToAccountModalOpen(false)}
        onConfirm={async (e) => {
          try {
            const res = await fetch(API_URL + `/api/v1/be/teams/account/share-account`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
                "Accept": "application/json",
              },
              body: JSON.stringify({
                team_id: id,
                account_number: selectedAccountNumber,
                account_status: e.status,
                balance: e.quymo,
              }),
            });
            const data = await res.json();
            // Kiểm tra nếu API trả về lỗi (isError được set ở hàm addTeamTradeAccount)
            if (data && data.isError) {
              // Kiểm tra lỗi validation riêng cho balance
              if (data.errors?.balance && data.errors.balance.length > 0) {
                toast.error(t("Teams_dashboard.addTradeAccountToTeam.balanceFieldRequired"));
                setIsAddNewInfoToAccountModalOpen(true);
              } else {
                // Các trường hợp lỗi hệ thống hoặc lỗi khác từ backend
                toast.error(data.message || t("Teams_dashboard.addTradeAccountToTeam.error"));
              }
            } else if (data) {
              // Nếu API thành công (không có isError)
              toast.success(t("Teams_dashboard.addTradeAccountToTeam.success"));
              closeLinkAccountModal();
            }

          }
          finally {
            fetchTeamAccounts(Number(id));
            setIsAddNewInfoToAccountModalOpen(false);
          }
        }}
        accountData={myTradeAccounts.find(acc => acc.account_number === selectedAccountNumber) || null}
      />

      <Modal
        open={isNewAccountModalOpen}
        onCancel={closeNewAccountModal}
        footer={null}
        closable={false}
        centered
        width={420}
        className="team-accounts-tab__new-account-modal"
      >
        <div className="team-accounts-tab__link-modal-header flex items-center justify-between pb-4 mb-4">
          <p className="team-accounts-tab__link-modal-title text-base font-bold m-0">
            {t("NewAccount")}
          </p>
          <button
            type="button"
            onClick={closeNewAccountModal}
            aria-label={t("Cancel")}
            className="team-accounts-tab__link-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-col gap-4 mb-4">
          <div className="flex flex-col gap-1.5">
            <label className="team-accounts-tab__new-account-label text-xs font-semibold uppercase tracking-wide">
              {t("AccountNumber")}
            </label>
            <input
              type="text"
              value={newAccountId}
              onChange={(e) => setNewAccountId(e.target.value)}
              className="team-accounts-tab__new-account-input w-full text-sm"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="team-accounts-tab__new-account-label text-xs font-semibold uppercase tracking-wide">
              {t("NickName")}
            </label>
            <input
              type="text"
              value={newAccountNickname}
              onChange={(e) => setNewAccountNickname(e.target.value)}
              className="team-accounts-tab__new-account-input w-full text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="team-accounts-tab__new-account-label text-xs font-semibold uppercase tracking-wide">
              {t("Balance_Size")}
            </label>
            <Select
              size="large"
              placeholder={t("Balance_Size")}
              value={newAccountBalance || undefined}
              onChange={(value) => setNewAccountBalance(value)}
              options={[
                { value: "10000", label: "10,000" },
                { value: "20000", label: "20,000" },
                { value: "50000", label: "50,000" },
                { value: "100000", label: "100,000" },
                { value: "200000", label: "200,000" },
              ]}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="team-accounts-tab__new-account-label text-xs font-semibold uppercase tracking-wide">
              {t("Platform")}
            </label>
            <Select
              size="large"
              placeholder={t("Platform")}
              value={newAccountPlatform || undefined}
              onChange={(value) => setNewAccountPlatform(value)}
              options={[
                { value: "mt4", label: "MT4" },
                { value: "mt5", label: "MT5" },
                { value: "ctrader", label: "cTrader" },
              ]}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="team-accounts-tab__new-account-label text-xs font-semibold uppercase tracking-wide">
              {t("Broker")}
            </label>
            <Select
              size="large"
              placeholder={t("Broker")}
              value={newAccountBroker || undefined}
              onChange={(value) => setNewAccountBroker(value)}
              options={brokerOptions}
            />
          </div>
          {
            newAccountBroker === "FTMO" && (
              <div className="flex flex-col gap-1.5">
                <label className="team-accounts-tab__new-account-label text-xs font-semibold uppercase tracking-wide">
                  {t("Account_Type")}
                </label>
                <Select
                  size="large"
                  placeholder={t("Account_Type")}
                  value={newAccountType || undefined}
                  onChange={(value) => setNewAccountType(value)}
                  options={[
                    { value: "Challenge", label: "Challenge" },
                    { value: "Verification", label: "Verification" },
                    { value: "Funded", label: "Funded" },
                    { value: "Demo", label: "Demo" },
                  ]}
                />
              </div>
            )
          }
        </div>

        <div className="team-accounts-tab__link-modal-footer flex items-center justify-end pt-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={closeNewAccountModal}
              className="team-accounts-tab__link-modal-btn team-accounts-tab__link-modal-btn--cancel px-4 py-2 rounded-lg text-sm font-medium"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={handleSaveNewAccount}
              disabled={savingNewAccount}
              className="team-accounts-tab__link-modal-btn team-accounts-tab__link-modal-btn--share px-4 py-2 rounded-lg text-sm font-medium"
            >
              {t("Save")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!accountToDelete}
        onCancel={() => setAccountToDelete(null)}
        footer={null}
        closable={false}
        centered
        width={420}
        className="team-accounts-tab__delete-modal"
      >
        <div className="flex flex-col items-start text-left">
          <div className="team-accounts-tab__delete-modal-icon flex items-center justify-center mb-4">
            <AlertTriangle size={24} />
          </div>
          <p className="team-accounts-tab__delete-modal-title text-base font-semibold m-0 mb-2">
            {t("DeleteTradeAccountTitle")}
          </p>
          <p className="team-accounts-tab__delete-modal-desc text-sm m-0 mb-1 leading-relaxed">
            {t("DeleteTradeAccountDesc", {
              name: accountToDelete?.nickname || accountToDelete?.account_number,
              account_number: accountToDelete?.account_number,
            })}
          </p>
          <p className="team-accounts-tab__delete-modal-warning text-sm m-0 mb-5 leading-relaxed">
            {t("DeleteTradeAccountWarning")}
          </p>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setAccountToDelete(null)}
            className="team-accounts-tab__link-modal-btn team-accounts-tab__link-modal-btn--cancel px-4 py-2 rounded-lg text-sm font-medium"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirmDeleteAccount}
            className="team-accounts-tab__delete-modal-btn flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium"
          >
            <Trash2 size={14} />
            {t("DeleteAccount")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default TeamAccountsTab;
